/**
 * Day 37 — Mobile Clinical Chart Entry (dynamic form + offline + history)
 * Renders a chart form from its server-side field schema, validates required
 * fields, queues the entry for offline-safe sync, and shows current-shift
 * history. Works for bowel, fluid intake, sleep, behaviour, seizure & pain.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker, TrinitiTimePicker, TrinitiDateTimePicker } from '@/components/triniti-datetime';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Save, ClipboardList, RefreshCw, History, Camera, ShieldAlert, Trash2 } from 'lucide-react';
import { queueOfflineAction, ACTION_TYPES } from '@/lib/offline/actionQueue';
import { syncAllActions, isOnline } from '@/lib/offline/syncEngine';
import { SyncStatusBadge, OfflineIndicator, SYNC_STATUS } from '@/components/mobile/SyncStatusBadge';
import MobileAccessDenied from '@/components/mobile/MobileAccessDenied';

const prettify = (v) => (typeof v === 'string' ? v.replace(/_/g, ' ') : v);

export default function MobileChartEntryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('participantId') || '';
  const templateCode = searchParams.get('type') || '';
  const shiftId = searchParams.get('shiftId') || '';

  const [online, setOnline] = useState(isOnline());
  const [schema, setSchema] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [accessDenied, setAccessDenied] = useState(false);
  const [photos, setPhotos] = useState([]); // {dataUrl, filename, bodyArea}

  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Load form schema + seed default datetimes
  useEffect(() => {
    if (!templateCode) return;
    api.get(`/mobile/charts/form/${templateCode}`)
      .then(res => {
        const s = res.data?.data;
        setSchema(s);
        const init = {};
        const nowLocal = new Date().toISOString().slice(0, 16);
        (s?.fields || []).forEach(f => {
          if (f.type === 'datetime') init[f.key] = nowLocal;
          else if (f.type === 'boolean') init[f.key] = false;
          else init[f.key] = '';
        });
        setForm(init);
      })
      .catch(err => {
        if (err.response?.status === 403) setAccessDenied(true);
        else toast.error('Could not load chart form');
      });
  }, [templateCode]);

  const loadHistory = useCallback(() => {
    if (!online || !participantId || !templateCode) return;
    api.get('/mobile/charts/entries', { params: { participantId, templateCode, shiftId: shiftId || undefined } })
      .then(res => setHistory(res.data?.data || []))
      .catch(() => {});
  }, [online, participantId, templateCode, shiftId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const buildValues = () => {
    const values = {};
    for (const f of (schema?.fields || [])) {
      const raw = form[f.key];
      if (f.type === 'boolean') { values[f.key] = !!raw; continue; }
      if (raw === '' || raw === undefined || raw === null) continue;
      if (f.type === 'number' || f.type === 'rating') values[f.key] = Number(raw);
      else if (f.type === 'datetime') values[f.key] = new Date(raw).toISOString();
      else values[f.key] = raw;
    }
    return values;
  };

  const validate = (values) => {
    for (const f of (schema?.fields || [])) {
      if (f.required && f.type !== 'boolean') {
        const v = values[f.key];
        if (v === undefined || v === null || v === '') {
          toast.error(`${f.label} is required`);
          return false;
        }
      }
    }
    return true;
  };

  const capturePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm('Privacy notice: clinical photos may contain sensitive health information. Only capture the wound/clinical area, avoid faces, and ensure consent. Continue?')) {
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhotos(p => [...p, { dataUrl: reader.result, filename: file.name || 'wound.jpg', bodyArea: form.wound_location || '' }]);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const uploadPhotos = async (entryId) => {
    for (const ph of photos) {
      const [, mime] = /data:(.*?);base64,/.exec(ph.dataUrl) || [];
      const b64 = ph.dataUrl.split(',')[1];
      try {
        await api.post('/mobile/charts/wound-photo', {
          clientActionId: (window.crypto?.randomUUID?.() || `wp-${Date.now()}-${Math.random()}`),
          participantId, entryId, shiftId: shiftId || null,
          bodyArea: ph.bodyArea || form.wound_location || null,
          filename: ph.filename, mimeType: mime || 'image/jpeg', dataBase64: b64,
        });
      } catch (err) {
        toast.error('A photo failed to upload');
      }
    }
  };

  const save = async () => {
    const values = buildValues();
    if (!validate(values)) return;
    setSaving(true);
    setSyncStatus(SYNC_STATUS.PENDING);
    try {
      // Day 38 — wound charts with photos go online-direct so we get the entry
      // id to attach durable photos. Falls back to the offline queue otherwise.
      if (schema?.supportsPhoto && photos.length > 0 && online) {
        const res = await api.post('/mobile/charts/entry', {
          clientActionId: (window.crypto?.randomUUID?.() || `ce-${Date.now()}`),
          participantId, templateCode, shiftId: shiftId || null, values,
        });
        const entryId = res.data?.data?.id;
        await uploadPhotos(entryId);
        setSyncStatus(SYNC_STATUS.SYNCED);
        toast.success('Chart entry & photos saved');
        setPhotos([]);
        loadHistory();
        setSaving(false);
        return;
      }

      await queueOfflineAction(ACTION_TYPES.CHART_ENTRY, {
        participantId, templateCode, shiftId: shiftId || null, values,
        timestamp: new Date().toISOString(),
      });
      toast.success('Chart entry saved locally', { description: online ? 'Syncing…' : 'Will sync when online' });
      if (online) {
        setSyncStatus(SYNC_STATUS.SYNCING);
        const results = await syncAllActions();
        if (results.forbidden > 0) { setSyncStatus(SYNC_STATUS.FAILED); setAccessDenied(true); }
        else if (results.synced > 0) {
          setSyncStatus(SYNC_STATUS.SYNCED);
          toast.success('Chart entry synced');
          loadHistory();
        } else if (results.failed > 0 || results.conflicts > 0) {
          setSyncStatus(SYNC_STATUS.FAILED);
          toast.error('Could not save entry', { description: 'Check required fields and retry.' });
        }
      }
    } catch (err) {
      setSyncStatus(SYNC_STATUS.FAILED);
      toast.error('Failed to save chart entry');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f) => {
    const val = form[f.key];
    switch (f.type) {
      case 'textarea':
        return <Textarea value={val || ''} onChange={(e) => setField(f.key, e.target.value)} rows={2} className="resize-none" data-testid={`field-${f.key}`} />;
      case 'number':
      case 'rating':
        return <Input type="number" min={f.min} max={f.max} value={val ?? ''} onChange={(e) => setField(f.key, e.target.value)} data-testid={`field-${f.key}`} />;
      case 'datetime':
        return <TrinitiDateTimePicker value={val || ''} onChange={(v) => setField(f.key, v)} testId={`field-${f.key}`} />;
      case 'date':
        return <TrinitiDatePicker value={val || ''} onChange={(v) => setField(f.key, v)} testId={`field-${f.key}`} />;
      case 'time':
        return <TrinitiTimePicker value={val || ''} onChange={(v) => setField(f.key, v)} testId={`field-${f.key}`} />;
      case 'boolean':
        return (
          <div className="flex items-center gap-2">
            <Switch checked={!!val} onCheckedChange={(c) => setField(f.key, c)} data-testid={`field-${f.key}`} />
            <span className="text-sm text-muted-foreground">{val ? 'Yes' : 'No'}</span>
          </div>
        );
      case 'select':
        return (
          <Select value={val || undefined} onValueChange={(v) => setField(f.key, v)}>
            <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder={`Select ${f.label.toLowerCase()}`} /></SelectTrigger>
            <SelectContent>
              {(f.options || []).map(o => <SelectItem key={o} value={o}>{prettify(o)}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      default:
        return <Input value={val || ''} onChange={(e) => setField(f.key, e.target.value)} data-testid={`field-${f.key}`} />;
    }
  };

  return (
    <>
      {accessDenied && <MobileAccessDenied />}
      <div className={`min-h-screen bg-background pb-24 ${accessDenied ? 'hidden' : ''}`} data-testid="mobile-chart-entry-page">
        <div className="sticky top-0 z-10 bg-primary text-primary-foreground px-4 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary/80" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                <h1 className="text-lg font-bold">{schema?.name || 'Chart Entry'}</h1>
              </div>
              <p className="text-xs opacity-80">Record a clinical observation</p>
            </div>
          </div>
        </div>

        <div className="px-4 pt-4 space-y-2">
          <OfflineIndicator isOffline={!online} />
          {syncStatus && <SyncStatusBadge status={syncStatus} />}
        </div>

        <div className="p-4 space-y-4">
          <Card>
            <CardContent className="p-4 space-y-4">
              {(schema?.fields || []).map(f => (
                <div className="space-y-2" key={f.key}>
                  <Label>{f.label}{f.required ? ' *' : ''}</Label>
                  {renderField(f)}
                </div>
              ))}
              {!schema && <p className="text-sm text-muted-foreground">Loading form…</p>}
            </CardContent>
          </Card>

          {schema?.supportsPhoto && (
            <Card className="border-rose-500/30" data-testid="wound-photo-section">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Camera className="h-4 w-4 text-rose-600" />
                  Wound photos
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-3">
                <div className="flex items-start gap-2 rounded-md bg-rose-500/10 px-3 py-2 text-xs text-rose-700">
                  <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
                  <span><strong>Privacy notice:</strong> clinical photos hold sensitive health data. Capture only the wound area, avoid faces, and confirm consent. Photos are stored securely and access is restricted &amp; audited.</span>
                </div>
                <input type="file" accept="image/*" capture="environment" onChange={capturePhoto}
                       className="block w-full text-xs" data-testid="wound-photo-input" />
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2" data-testid="wound-photo-previews">
                    {photos.map((p, i) => (
                      <div key={i} className="relative">
                        <img src={p.dataUrl} alt={`wound-${i}`} className="h-20 w-full object-cover rounded" />
                        <button onClick={() => setPhotos(ph => ph.filter((_, idx) => idx !== i))}
                                className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5"
                                data-testid={`remove-photo-${i}`}>
                          <Trash2 className="h-3 w-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {!online && <p className="text-[11px] text-muted-foreground">Photos upload when you are online. Save the entry now; reopen to attach photos.</p>}
              </CardContent>
            </Card>
          )}

          {history.length > 0 ? (
            <Card data-testid="chart-history">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <History className="h-4 w-4 text-muted-foreground" />
                  {shiftId ? 'This shift' : 'Recent'} ({history.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-2">
                {history.map(h => (
                  <div key={h.id} className="rounded-md border bg-muted/30 p-2 text-xs" data-testid={`history-${h.id}`}>
                    <span className="font-medium">{(h.entry_date || h.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                    {h.summary ? <span className="ml-2 text-muted-foreground">{h.summary}</span>
                      : h.notes ? <span className="ml-2 text-muted-foreground">{h.notes}</span> : null}
                  </div>
                ))}
              </CardContent>
            </Card>
          ) : (
            schema && online && (
              <p className="text-xs text-muted-foreground px-1" data-testid="chart-history-empty">
                No entries yet{shiftId ? ' for this shift' : ''}.
              </p>
            )
          )}
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4">
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => navigate(-1)}>Cancel</Button>
            <Button className="flex-1" onClick={save} disabled={saving || !schema} data-testid="save-chart-entry-btn">
              {saving ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Saving…</> : <><Save className="h-4 w-4 mr-2" />{online ? 'Save & Sync' : 'Save Draft'}</>}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
